import { useState } from 'react';
import { usePriceListColumnNames, useSavePriceListColumnNames } from '@/api/preis-tool/preis-tool.hooks';
import type { PriceListColumnName, PriceListColumnNameTarget } from '@/api/preis-tool/preis-tool.types';
import { DsAlert, DsButton, DsInput } from '@/modules/internal-v2/ds';
import { fmtDateTime } from '../helpers';
import { FIELDS, normaliseColumnName, targetLabel } from '../price-list-file';

const TARGETS: PriceListColumnNameTarget[] = [...FIELDS, 'ignore'];

const HINTS: Record<PriceListColumnNameTarget, string> = {
  itemNumber: 'wird mit der EOS-Artikelnummer verglichen (meist die Herstellernummer), nicht mit der Nummer des Lieferanten',
  ean: 'führende Nullen zählen nicht',
  netPrice: 'der Preis, der als Fixpreis übernommen wird',
  uvpNet: '',
  uvpGross: '',
  quantity: 'Stückzahl, die der Lieferant selbst hat; zählt im Angebot unter „Lager“',
  description: 'nur zur Anzeige in der Vorschau und in der CSV der nicht übernommenen Zeilen',
  ignore: 'Spalten mit diesen Überschriften werden nie zugeordnet',
};

const sameList = (a: PriceListColumnName[], b: PriceListColumnName[]) =>
  a.length === b.length && a.every((n, i) => n.name === b[i].name && n.field === b[i].field);

interface PriceListColumnNamesEditorProps {
  /** After a save; the upload computes its suggestion again. */
  onSaved: () => void;
}

/**
 * The column names of the price list upload (EOS-59), shared by everyone:
 * which header texts EOS assigns to which field. Edited as a draft, stored
 * as a whole with "Speichern".
 */
export function PriceListColumnNamesEditor({ onSaved }: PriceListColumnNamesEditorProps) {
  const { data, isLoading, isError } = usePriceListColumnNames();
  const save = useSavePriceListColumnNames();
  // `null` = no change yet, the stored list is shown.
  const [draft, setDraft] = useState<PriceListColumnName[] | null>(null);
  const [newNames, setNewNames] = useState<Partial<Record<PriceListColumnNameTarget, string>>>({});
  const [addError, setAddError] = useState<{ target: PriceListColumnNameTarget; text: string } | null>(null);

  if (isLoading) return <div className="pt-fix-empty">Lade Spaltennamen…</div>;
  if (isError || !data) return <DsAlert severity="error">Die Spaltennamen konnten nicht geladen werden.</DsAlert>;

  const names = draft ?? data.names;
  const byTarget = new Map<PriceListColumnNameTarget, PriceListColumnName[]>(TARGETS.map((t) => [t, []]));
  for (const entry of names) byTarget.get(entry.field)?.push(entry);

  const add = (target: PriceListColumnNameTarget) => {
    const name = (newNames[target] ?? '').trim();
    const key = normaliseColumnName(name);
    if (!key) {
      setAddError({ target, text: 'Der Name braucht mindestens einen Buchstaben oder eine Ziffer.' });
      return;
    }
    const existing = names.find((n) => normaliseColumnName(n.name) === key);
    if (existing) {
      setAddError({
        target,
        text:
          existing.field === target
            ? `„${existing.name}“ steht hier schon.`
            : `„${existing.name}“ steht schon bei „${targetLabel(existing.field)}“. Dort zuerst entfernen.`,
      });
      return;
    }
    setDraft([...names, { name, field: target }]);
    setNewNames({ ...newNames, [target]: '' });
    setAddError(null);
  };

  const remove = (entry: PriceListColumnName) => setDraft(names.filter((n) => n !== entry));
  const changed = !sameList(names, data.names);

  return (
    <div className="pt-form">
      <p className="pt-colnames-rule">
        Eine Spalte wird erkannt, wenn ihre Überschrift einem Namen gleicht oder ihn als ganzes Wort enthält („Preis EUR“
        enthält „Preis“). Groß- und Kleinschreibung, Punkte und Bindestriche zählen nicht. Gleicht die Überschrift einem
        Namen genau, gilt dieser, sonst der längere. Die Liste gilt für alle Nutzer.
      </p>
      <div className="pt-colnames">
        {TARGETS.map((target) => (
          <div key={target} className="pt-colnames-group">
            <div className="label">
              {targetLabel(target)}
              {HINTS[target] && <span className="hint">{HINTS[target]}</span>}
            </div>
            <div className="pt-dsettings-margins">
              {byTarget.get(target)!.map((entry) => (
                <span key={entry.name} className="ds-chip">
                  {entry.name}
                  <button
                    type="button"
                    className="pt-dsettings-chip-x"
                    onClick={() => remove(entry)}
                    title="Entfernen"
                    aria-label={`${entry.name} entfernen`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {byTarget.get(target)!.length === 0 && <span className="pt-fix-hint">keine</span>}
              <span className="pt-dsettings-add">
                <DsInput
                  value={newNames[target] ?? ''}
                  onChange={(e) => setNewNames({ ...newNames, [target]: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') add(target);
                  }}
                  placeholder="Spaltenname"
                  aria-label={`Neuer Spaltenname für ${targetLabel(target)}`}
                  maxLength={100}
                  style={{ width: 150 }}
                />
                <DsButton size="small" onClick={() => add(target)} disabled={!(newNames[target] ?? '').trim()}>
                  Hinzufügen
                </DsButton>
              </span>
            </div>
            {addError?.target === target && <span className="pt-colnames-error">{addError.text}</span>}
          </div>
        ))}
      </div>
      {save.isError && <DsAlert severity="error">Die Spaltennamen konnten nicht gespeichert werden.</DsAlert>}
      <div className="pt-dsettings-status">
        <span>
          {data.updatedAt
            ? `Zuletzt geändert am ${fmtDateTime(data.updatedAt)}${data.updatedByEmail ? ` von ${data.updatedByEmail}` : ''}`
            : 'Es gelten die Standardnamen.'}
        </span>
        <span className="pt-colnames-actions">
          <DsButton size="small" variant="ghost" onClick={() => setDraft(data.defaults)} disabled={save.isPending}>
            Auf Standard zurücksetzen
          </DsButton>
          <DsButton
            size="small"
            variant="primary"
            onClick={() =>
              save.mutate(
                { names },
                {
                  onSuccess: () => {
                    setDraft(null);
                    onSaved();
                  },
                },
              )
            }
            disabled={!changed || save.isPending}
          >
            {save.isPending ? 'Speichere…' : 'Speichern'}
          </DsButton>
        </span>
      </div>
    </div>
  );
}
